import type { SessionDetail } from "@/lib/api";

export const SESSION_DONE_FOR_ARCHIVE = new Set([
  "ended",
  "recorded",
  "completed",
  "review",
  "failed",
  "upload_failed",
]);

/** Archives exist but SessionPostprocess has not written peaks yet. */
export function archivePeaksPending(detail: SessionDetail | null, waveBarCount: number): boolean {
  if (!detail) return false;
  const ws = String(detail.waveformStatus || "").toLowerCase();
  if (ws === "failed") return false;
  if (ws === "ready" && waveBarCount > 0) return false;
  if (!SESSION_DONE_FOR_ARCHIVE.has(String(detail.status || "").toLowerCase())) return false;
  const keys = detail.recordingKeys || {};
  const hasArchive = Boolean(
    detail.timelineAudioUrl
    || detail.audioUrl
    || detail.archiveUserAudio
    || detail.archiveBotAudio
    || keys.userAudio
    || keys.botAudio
    || keys.userVideo
    || keys.botVideo,
  );
  if (!hasArchive) return false;
  return waveBarCount === 0 || ws !== "ready";
}

export function videoEgressArchiveProcessing(detail: SessionDetail | null): boolean {
  const ve = String(detail?.videoEgressStatus || "").toLowerCase();
  return ve === "starting" || ve === "pending" || ve === "uploading";
}

export type ArchivePlaybackState = {
  processing: boolean;
  unavailable: boolean;
  message: string | null;
};

/** When true, show a subtle hint — not while peaks or egress video are still in flight. */
export function computeArchivePlaybackState(
  detail: SessionDetail | null,
  waveBarCount: number,
  videoRemoved: boolean,
): ArchivePlaybackState {
  const idle: ArchivePlaybackState = { processing: false, unavailable: false, message: null };
  if (!detail) return idle;

  const status = String(detail.status || "").toLowerCase();
  if (!SESSION_DONE_FOR_ARCHIVE.has(status)) return idle;

  if (archivePeaksPending(detail, waveBarCount) || videoEgressArchiveProcessing(detail)) {
    return { processing: true, unavailable: false, message: null };
  }

  const keys = detail.recordingKeys || {};
  const hasAudioArchive = Boolean(
    (detail.timelineAudioUrl || "").trim()
    || (detail.audioUrl || "").trim()
    || detail.archiveUserAudio
    || detail.archiveBotAudio
    || keys.userAudio
    || keys.botAudio,
  );
  const hasVideoArchive =
    !videoRemoved
    && Boolean(
      (detail.userVideoUrl || "").trim()
      || (detail.botVideoUrl || "").trim()
      || keys.userVideo
      || keys.botVideo,
    );

  if (hasAudioArchive || hasVideoArchive) return idle;

  const mode = String(detail.agentMode || "").toLowerCase();
  const clientArchiveOn = detail.clientSessionArchiveEnabled === true;
  const archiveSourceClient = String(detail.archiveSource || "").toLowerCase() === "client";

  if (mode === "liveavatar" && clientArchiveOn && !archiveSourceClient) {
    return {
      processing: false,
      unavailable: true,
      message:
        "Browser session archive did not finish — audio and camera recordings are not available for playback. Try a new call after a hard refresh of the conversation app.",
    };
  }

  if (mode === "liveavatar" && !clientArchiveOn) {
    return {
      processing: false,
      unavailable: true,
      message:
        "No session recording in Pulse — LiveAvatar FULL archives audio and video only when Client session archive is enabled on the agent.",
    };
  }

  const expectsEgress =
    mode !== "liveavatar"
    || Boolean(detail.videoEgressStatus)
    || keys.userAudio
    || keys.botAudio
    || keys.userVideo
    || keys.botVideo;

  if (!expectsEgress) return idle;

  return {
    processing: false,
    unavailable: true,
    message:
      "Session recording is not available for playback. Track archives may have failed or not finished uploading.",
  };
}
